import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Hook otimizado que detecta quando a aba volta ao foco e força atualização dos dados
 * Fase 1: Refetch otimizado - apenas se necessário
 * Sprint 2 - Fase 3: Migração 100% Realtime
 */
export const usePageVisibility = () => {
  const queryClient = useQueryClient();
  const lastVisibilityChangeRef = useRef<Date>(new Date());

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const now = new Date();
        const timeSinceLastVisibility = now.getTime() - lastVisibilityChangeRef.current.getTime();
        const oneMinute = 60 * 1000;
        
        // Apenas refetch se a tab ficou em background por mais de 1 minuto
        if (timeSinceLastVisibility > oneMinute) {
          console.log(`📱 Aba voltou ao foco após ${Math.floor(timeSinceLastVisibility / 1000)}s - sincronizando...`);
          
          // Apenas 1 refetch por query para sincronização
          queryClient.invalidateQueries({ 
            queryKey: ['orders-map'], 
            refetchType: 'active',
            exact: true
          });
          queryClient.invalidateQueries({ 
            queryKey: ['production-steps'], 
            refetchType: 'active'
          });
          queryClient.invalidateQueries({ 
            queryKey: ['all-production-steps'], 
            refetchType: 'active',
            exact: true
          });
          queryClient.invalidateQueries({ 
            queryKey: ['production_step_events'], 
            refetchType: 'active'
          });
          queryClient.invalidateQueries({ 
            queryKey: ['observacoes'], 
            refetchType: 'active'
          });
          // Logística - usar 'all' para capturar variações parametrizadas
          queryClient.invalidateQueries({ 
            queryKey: ['logistics-orders'], 
            refetchType: 'all'
          });
          // Arte jobs (corrigido: query usa 'arte-jobs' com hífen)
          queryClient.invalidateQueries({ 
            queryKey: ['arte-jobs'], 
            refetchType: 'all'
          });
          // Queries sem polling — safety net
          queryClient.invalidateQueries({ 
            queryKey: ['integrity-alerts'], 
            refetchType: 'active',
            exact: true
          });
          queryClient.invalidateQueries({ 
            queryKey: ['painel'], 
            refetchType: 'active'
          });
        } else {
          console.log('📱 Aba voltou ao foco rapidamente - sem necessidade de refetch');
        }
        
        lastVisibilityChangeRef.current = now;
      } else {
        // Aba saiu do foco - registrar timestamp
        lastVisibilityChangeRef.current = new Date();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [queryClient]);
};
